// ============================================================
// WebSocketPlugin — viewer counter, commands, playback tracking
// Connects to wss://chat.goodgame.ru/counter/
// ============================================================
import type { IPlugin, PluginContext } from '../core/types';

export interface WebSocketPluginOptions {
  /** WebSocket URL (default: wss://chat.goodgame.ru/counter/) */
  url?: string;
  /** Ping interval in ms (default: 30000) */
  pingInterval?: number;
  /** Reconnect delay in ms (default: 3000) */
  reconnectDelay?: number;
}

const DEFAULT_URL = 'wss://chat.goodgame.ru/counter/';
const DEFAULT_PING_INTERVAL = 30_000;
const DEFAULT_RECONNECT_DELAY = 3_000;
const PONG_TIMEOUT = 3_000;

export class WebSocketPlugin implements IPlugin {
  readonly name = 'websocket';

  private ctx: PluginContext | null = null;
  private ws: WebSocket | null = null;
  private unsubs: Array<() => void> = [];
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private pongTimer: ReturnType<typeof setTimeout> | null = null;
  private waitingPong = false;
  private destroyed = false;
  private userId: number | undefined = undefined;

  constructor(private options: WebSocketPluginOptions = {}) {}

  init(ctx: PluginContext): void {
    this.ctx = ctx;
    this.destroyed = false;
    // Seed userId from config if pre-loaded
    if (ctx.config.apiData?.user?.user_id) {
      this.userId = ctx.config.apiData.user.user_id;
    }
    this.connect();
    this.bindEvents();
    ctx.logger.debug('WebSocketPlugin initialized');
  }

  destroy(): void {
    this.destroyed = true;
    this.unsubs.forEach(fn => fn());
    this.unsubs = [];
    this.disconnect();
    this.ctx = null;
    this.userId = undefined;
  }

  // --- Private ---

  private connect(): void {
    if (this.destroyed) return;

    const url = this.options.url || DEFAULT_URL;
    try {
      this.ws = new WebSocket(url);
    } catch (e) {
      this.ctx?.logger.warn('WebSocket connection failed:', e);
      this.scheduleReconnect();
      return;
    }

    this.ws.onmessage = this.onMessage;
    this.ws.onclose = this.onClose;
    this.ws.onerror = () => {}; // onclose will handle reconnect

    if (!this.pingTimer) {
      this.pingTimer = setInterval(
        () => this.ping(),
        this.options.pingInterval || DEFAULT_PING_INTERVAL,
      );
    }
  }

  private disconnect(): void {
    if (this.pingTimer) {
      clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
    if (this.pongTimer) {
      clearTimeout(this.pongTimer);
      this.pongTimer = null;
    }
    if (this.ws) {
      this.ws.onclose = null;
      this.ws.onmessage = null;
      this.ws.onerror = null;
      this.ws.close();
      this.ws = null;
    }
  }

  private scheduleReconnect(): void {
    if (this.destroyed) return;
    const delay = this.options.reconnectDelay || DEFAULT_RECONNECT_DELAY;
    this.ctx?.logger.debug(`WebSocket reconnecting in ${delay}ms`);
    setTimeout(() => this.connect(), delay);
  }

  private bindEvents(): void {
    const { eventBus, store } = this.ctx!;

    this.unsubs.push(
      eventBus.on('playing', () => this.sendViewing()),
      eventBus.on('stopped', () => this.send('unview', { channel: store.getState().streamKey })),
      eventBus.on('api:user', (user) => {
        this.userId = user.user_id;
      }),
    );
  }

  private ping(): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;

    this.waitingPong = true;
    const state = this.ctx?.store.getState();
    this.send('ping', {
      channel: state?.streamKey,
      state: state?.isPlaying ? 1 : 0,
    });

    this.pongTimer = setTimeout(() => {
      if (this.waitingPong) {
        this.ctx?.logger.warn('No pong received, reconnecting');
        this.onClose();
      }
    }, PONG_TIMEOUT);
  }

  private sendViewing(): void {
    const state = this.ctx?.store.getState();
    const apiData = this.ctx?.config.apiData;

    const data: Record<string, unknown> = {
      channel: state?.streamKey,
      v: 2,
      active: state?.isPlaying ? 1 : 0,
    };

    if (this.userId) {
      data.userId = this.userId;
    }
    if (apiData?.streamer_id) {
      data.streamerId = apiData.streamer_id;
    }
    if (apiData?.game_id) {
      data.gameId = apiData.game_id;
    }

    this.send('viewing', data);
  }

  private getViewers(): void {
    const state = this.ctx?.store.getState();
    this.send('get_all_viewers', { channel: state?.streamKey });
  }

  private onMessage = (event: MessageEvent): void => {
    let msg: { type: string; data?: any };
    try {
      msg = JSON.parse(event.data);
    } catch {
      return;
    }

    switch (msg.type) {
      case 'welcome':
        // On connection established, request viewer count and start viewing
        this.getViewers();
        if (this.ctx?.store.getState().isPlaying) {
          this.sendViewing();
        }
        break;

      case 'pong':
        this.waitingPong = false;
        if (this.pongTimer) {
          clearTimeout(this.pongTimer);
          this.pongTimer = null;
        }
        break;

      case 'viewers':
        if (msg.data?.count != null && this.ctx?.store.getState().isOnline) {
          this.ctx.store.setState({ viewers: msg.data.count });
          this.ctx.eventBus.emit('viewers:update', msg.data.count);
        }
        break;
    }
  };

  private onClose = (): void => {
    this.disconnect();
    this.ctx?.logger.warn('WebSocket closed, reconnecting...');
    this.scheduleReconnect();
  };

  private send(type: string, data: Record<string, unknown> = {}): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    try {
      this.ws.send(JSON.stringify({ type, data }));
    } catch (e) {
      this.ctx?.logger.warn('WebSocket send error:', e);
    }
  }
}
